import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { AlertCircle, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { changePassword, login, type ApiError } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";

const schema = z
  .object({
    currentPassword: z.string().min(1, "Requerido"),
    newPassword: z
      .string()
      .min(10, "Mínimo 10 caracteres")
      .refine((v) => /[A-Z]/.test(v), "Debe incluir al menos una mayúscula")
      .refine((v) => /[0-9]/.test(v), "Debe incluir al menos un número")
      .refine((v) => /[^A-Za-z0-9]/.test(v), "Debe incluir al menos un símbolo"),
    confirmPassword: z.string(),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    message: "Las passwords no coinciden",
    path: ["confirmPassword"],
  });

type FormValues = z.infer<typeof schema>;

export function ChangePasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as { email?: string; currentPassword?: string };
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const changeToken = useAuthStore((s) => s.changeToken);
  const setAccessToken = useAuthStore((s) => s.setAccessToken);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      currentPassword: state.currentPassword ?? "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  if (!changeToken) {
    navigate("/login", { replace: true });
    return null;
  }

  async function onSubmit(values: FormValues) {
    setError(null);
    setSubmitting(true);
    try {
      await changePassword(changeToken!, values.currentPassword, values.newPassword);
      // Re-login automático con la nueva password
      if (state.email) {
        const resp = await login(state.email, values.newPassword);
        if (resp.token_type === "bearer") {
          setAccessToken(resp.access_token);
          navigate("/", { replace: true });
          return;
        }
      }
      // Fallback: volver al login
      navigate("/login", { replace: true });
    } catch (e) {
      const api = e as ApiError;
      setError(api.message ?? "No se pudo cambiar la password");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <KeyRound size={20} />
          </div>
          <CardTitle>Cambiar password</CardTitle>
          <CardDescription>
            Es tu primer acceso — define una password personal antes de continuar.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="currentPassword">Password actual</Label>
              <Input
                id="currentPassword"
                type="password"
                autoComplete="current-password"
                {...register("currentPassword")}
              />
              {errors.currentPassword && (
                <span className="text-xs text-destructive">
                  {errors.currentPassword.message}
                </span>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="newPassword">Nueva password</Label>
              <Input
                id="newPassword"
                type="password"
                autoComplete="new-password"
                {...register("newPassword")}
              />
              {errors.newPassword && (
                <span className="text-xs text-destructive">{errors.newPassword.message}</span>
              )}
              <span className="text-[10px] text-muted-foreground">
                10+ caracteres, con mayúscula, número y símbolo.
              </span>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="confirmPassword">Confirmar password</Label>
              <Input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                {...register("confirmPassword")}
              />
              {errors.confirmPassword && (
                <span className="text-xs text-destructive">
                  {errors.confirmPassword.message}
                </span>
              )}
            </div>

            {error && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button type="submit" disabled={submitting}>
              {submitting ? "Cambiando…" : "Cambiar password"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
